import React, { useMemo } from "react";
import {
  Grid,
  TextField,
  FormControl,
  Select,
  Checkbox,
  ListItemText,
  MenuItem,
  FormHelperText,
  Typography,
  Chip,
  Box,
  Radio,
  RadioGroup,
  FormControlLabel,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { useTranslation } from "react-i18next";
import {
  getUserDateFormat,
  convertToDateFnsFormat,
} from "../../utils/formatDate";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import { format, parseISO, isValid } from "date-fns";

const RequiredStar = () => (
  <Typography component="span" color="error" sx={{ ml: 0.5 }}>
    *
  </Typography>
);

const FieldLabel = ({ label, required }) => (
  <Typography
    component="span"
    sx={{
      fontWeight: "400",
      fontFamily: "Roboto",
      fontSize: "16px",
      color: "#414958",
      mb: "8px",
    }}
  >
    {label}
    {required && <RequiredStar />}
  </Typography>
);

const WEEKDAYS = [
  { value: 0, label: "Sunday", short: "Sun" },
  { value: 1, label: "Monday", short: "Mon" },
  { value: 2, label: "Tuesday", short: "Tue" },
  { value: 3, label: "Wednesday", short: "Wed" },
  { value: 4, label: "Thursday", short: "Thu" },
  { value: 5, label: "Friday", short: "Fri" },
  { value: 6, label: "Saturday", short: "Sat" },
];

const MONTH_DAYS = Array.from({ length: 31 }, (_, i) => i + 1);

const getRepeatEveryOptionCount = (repitation) => {
  switch (repitation) {
    case "DAILY":
      return 6;
    case "WEEKLY":
      return 3;
    case "MONTHLY":
      return 11;
    default:
      return 0;
  }
};

const ScheduleDetailsForm = ({
  schedule,
  routes,
  vehicles,
  errors,
  handleChange,
  handleVehicleChange,
  isViewMode = false,
  updateRoute,
}) => {
  const { t } = useTranslation();

  const userDateFormat = getUserDateFormat();
  const repeatEveryOptions = Array.from(
    { length: getRepeatEveryOptionCount(schedule.repitation) },
    (_, index) => index + 1,
  );
  const repeatEveryValue =
    Number(schedule.repeatEvery) > 0 ? Number(schedule.repeatEvery) : "";

  const normalizedVehicleIds = useMemo(
    () =>
      (schedule.vehicleIds || [])
        .map((id) => Number(id))
        .filter((id) => Number.isFinite(id) && id > 0),
    [schedule.vehicleIds],
  );

  const vehicleNameById = useMemo(() => {
    const map = new Map();
    (vehicles || []).forEach((v) => {
      if (v?.id != null) {
        map.set(Number(v.id), v.name);
      }
    });
    (schedule.vehicleScheduleSummary || []).forEach((s) => {
      const id = Number(s?.vehicleId);
      if (Number.isFinite(id) && id > 0 && s?.vehicleName && !map.has(id)) {
        map.set(id, s.vehicleName);
      }
    });
    return map;
  }, [vehicles, schedule.vehicleScheduleSummary]);

  // Add these new lines:
  const getDatePlaceholder = () => {
    return userDateFormat;
  };

  const getDateInputFormat = () => {
    return convertToDateFnsFormat(userDateFormat);
  };

  const isEditingSchedule = Boolean(schedule?.id);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const todayDateString = format(today, "yyyy-MM-dd");
  const minimumEndDate =
    schedule.startDate && schedule.startDate > todayDateString
      ? schedule.startDate
      : todayDateString;

  // Handle weekly days selection
  const handleWeeklyDaysChange = (event) => {
    const value = event.target.value;
    handleChange({
      target: {
        name: "weeklyDays",
        value: typeof value === "string" ? value.split(",").map(Number) : value,
      },
    });
  };

  // Handle monthly days selection
  const handleMonthlyDaysChange = (event) => {
    const value = event.target.value;
    handleChange({
      target: {
        name: "monthlyDays",
        value: typeof value === "string" ? value.split(",").map(Number) : value,
      },
    });
  };

  // Handle onetime date selection
  const handleOnetimeDateChange = (newValue) => {
    handleChange({
      target: {
        name: "onetimeDate",
        value: newValue,
      },
    });
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Grid container spacing={4} sx={{ mb: 2 }}>
        <Grid item xs={12} md={3}>
          <FormControl fullWidth variant="outlined" error={!!errors.name}>
            <FieldLabel label={t("Name")} required={true} />
            <TextField
              name="name"
              value={schedule.name}
              onChange={handleChange}
              placeholder={t("Enter schedule name")}
              required
              fullWidth
              disabled={isViewMode}
              autoComplete="off"
              inputProps={{
                style: {
                  fontFamily: "Roboto",
                  fontWeight: 400,
                  fontSize: "16px",
                  lineHeight: "100%",
                  color: "#111214",
                },
              }}
              variant="outlined"
              InputLabelProps={{ shrink: true }}
              error={!!errors.name}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "8px",
                  maxHeight: "44px",
                  backgroundColor: "#fff",
                  "& fieldset": {
                    border: " 1px solid #647490",
                  },
                },
              }}
            />
            {errors.name && (
              <FormHelperText error>{errors.name}</FormHelperText>
            )}
          </FormControl>
        </Grid>
        <Grid item xs={12} md={3}>
          <FormControl fullWidth variant="outlined" error={!!errors.routeId}>
            <FieldLabel label={t("Route")} required={true} />
            <Select
              labelId="route-label"
              name="routeId"
              value={schedule.routeId}
              onChange={handleChange}
              required
              fullWidth
              IconComponent={(props) => (
                <svg
                  width="18"
                  height="10"
                  viewBox="0 0 18 10"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  style={{
                    marginRight: "10px",
                  }}
                >
                  <path
                    d="M1 1L9 9L17 1"
                    stroke="#20A8E0"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
              )}
              disabled={isViewMode || updateRoute}
              displayEmpty
              error={!!errors.routeId}
              renderValue={(selected) =>
                selected ? (
                  routes.find((r) => r.id === selected)?.name
                ) : (
                  <span style={{ color: "#aaa" }}>{t("Select Route")}</span>
                )
              }
              MenuProps={{
                PaperProps: {
                  sx: {
                    mt: 1,
                    maxHeight: 270,
                  },
                },
              }}
              sx={{
                borderRadius: "8px",
                maxHeight: "44px",
                fontFamily: "Roboto",
                fontWeight: 400,
                fontSize: "16px",
                color: "#111214",
                "& .MuiOutlinedInput-notchedOutline": {
                  border: "1px solid #647490",
                  borderRadius: "8px",
                },
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  border: "1px solid #647490",
                },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  border: "1px solid #647490",
                },
                "& .MuiSelect-select": {
                  fontFamily: "Roboto",
                  fontWeight: 400,
                  fontSize: "16px",
                  color: "#111214",
                },
              }}
            >
              <MenuItem value="" disabled>
                {t("Select Route")}
              </MenuItem>
              {routes?.map((route) => (
                <MenuItem key={route.id} value={route.id}>
                  {route.name}
                </MenuItem>
              ))}
            </Select>
            {errors.routeId && (
              <FormHelperText error>{errors.routeId}</FormHelperText>
            )}
          </FormControl>
        </Grid>

        <Grid item xs={12} md={2}>
          <FieldLabel label={t("Vehicle")} />
          <Select
            labelId="vehicle-label"
            name="vehicleIds"
            IconComponent={(props) => (
              <svg
                width="18"
                height="10"
                viewBox="0 0 18 10"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                style={{
                  marginRight: "10px",
                }}
              >
                <path
                  d="M1 1L9 9L17 1"
                  stroke="#20A8E0"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            )}
            value={normalizedVehicleIds}
            onChange={(e) => {
              const selectedOptions = e.target.value.filter(
                (value) => value !== "",
              );
              handleVehicleChange({
                target: { name: "vehicleIds", value: selectedOptions },
              });
            }}
            fullWidth
            multiple
            disabled={isViewMode}
            displayEmpty
            MenuProps={{
              PaperProps: {
                sx: {
                  maxHeight: 270,
                  mt: 1,
                },
              },
            }}
            renderValue={(selected) => {
              if (!selected.length) return t("Select Vehicle");
              const selectedNames = selected
                .map((id) => {
                  const n = Number(id);
                  return vehicleNameById.get(n) || null;
                })
                .filter(Boolean)
                .join(", ");
              return selectedNames || t("Select Vehicle");
            }}
            sx={{
              mt: "8px",
              borderRadius: "16px",
              maxHeight: "44px",
              fontFamily: "Roboto",
              fontWeight: 400,
              fontSize: "16px",
              color: "#111214",
              "& .MuiOutlinedInput-notchedOutline": {
                border: " 1px solid #647490",
                borderRadius: "8px",
              },
              "&:hover .MuiOutlinedInput-notchedOutline": {
                border: "1px solid #647490",
              },
              "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                border: "1px solid #647490",
              },
              "& .MuiSelect-select": {
                fontFamily: "Roboto",
                fontWeight: 400,
                fontSize: "16px",
                color: "#111214",
              },
            }}
          >
            <MenuItem value="" disabled>
              {t("Select Vehicle")}
            </MenuItem>
            {vehicles?.map((vehicle) => (
              <MenuItem key={vehicle.id} value={Number(vehicle.id)}>
                <Checkbox
                  checked={normalizedVehicleIds.includes(Number(vehicle.id))}
                  sx={{
                    color: "#20A8E0",
                    "&.Mui-checked": {
                      color: "#20A8E0",
                    },
                  }}
                />
                <ListItemText primary={vehicle.name} />
              </MenuItem>
            ))}
          </Select>
        </Grid>

        <Grid item xs={12} md={2}>
          <FormControl fullWidth variant="outlined" error={!!errors.repitation}>
            <FieldLabel label={t("Frequency")} required={true} />
            <Select
              labelId="repetition-label"
              name="repitation"
              value={schedule.repitation}
              onChange={handleChange}
              required
              fullWidth
              displayEmpty
              IconComponent={(props) => (
                <svg
                  width="18"
                  height="10"
                  viewBox="0 0 18 10"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  style={{
                    marginRight: "10px",
                  }}
                >
                  <path
                    d="M1 1L9 9L17 1"
                    stroke="#20A8E0"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
              )}
              disabled={isViewMode}
              error={!!errors.repitation}
              sx={{
                borderRadius: "8px",
                maxHeight: "44px",
                fontFamily: "Roboto",
                fontWeight: 400,
                fontSize: "16px",
                color: "#111214",
                "& .MuiOutlinedInput-notchedOutline": {
                  border: "1px solid #647490",
                  borderRadius: "8px",
                },
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  border: "1px solid #647490",
                },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  border: "1px solid #647490",
                },
                "& .MuiSelect-select": {
                  fontFamily: "Roboto",
                  fontWeight: 400,
                  fontSize: "16px",
                  color: "#111214",
                },
              }}
            >
              <MenuItem value="" disabled>
                {t("Select Frequency")}
              </MenuItem>
              <MenuItem value="DAILY">{t("Daily")}</MenuItem>
              <MenuItem value="WEEKLY">{t("Weekly")}</MenuItem>
              <MenuItem value="MONTHLY">{t("Monthly")}</MenuItem>
              <MenuItem value="ONETIME">{t("One time")}</MenuItem>
            </Select>
            {errors.repitation && (
              <FormHelperText error>{errors.repitation}</FormHelperText>
            )}
          </FormControl>
        </Grid>

        <Grid item xs={12} md={2}>
          <FormControl
            fullWidth
            variant="outlined"
            error={!!errors.repeatEvery}
          >
            <FieldLabel label={t("Repeat Every")} />
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: "10px",
              }}
            >
              <Select
                labelId="repeat-every-label"
                name="repeatEvery"
                value={repeatEveryValue}
                onChange={handleChange}
                fullWidth
                displayEmpty
                disabled={
                  isViewMode ||
                  !["DAILY", "WEEKLY", "MONTHLY"].includes(schedule.repitation)
                }
                IconComponent={(props) => (
                  <svg
                    width="18"
                    height="10"
                    viewBox="0 0 18 10"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    style={{
                      marginRight: "10px",
                    }}
                  >
                    <path
                      d="M1 1L9 9L17 1"
                      stroke="#20A8E0"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>
                )}
                error={!!errors.repeatEvery}
                sx={{
                  borderRadius: "8px",
                  maxHeight: "44px",
                  fontFamily: "Roboto",
                  fontWeight: 400,
                  minHeight: "44px",
                  fontSize: "16px",
                  color: "#111214",
                  "& .MuiOutlinedInput-notchedOutline": {
                    border: "1px solid #647490",
                    borderRadius: "8px",
                  },
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    border: "1px solid #647490",
                  },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    border: "1px solid #647490",
                  },
                  "& .MuiSelect-select": {
                    fontFamily: "Roboto",
                    fontWeight: 400,
                    fontSize: "16px",
                    color: "#111214",
                  },
                }}
              >
                <MenuItem value="" disabled />
                {repeatEveryOptions.map((value) => (
                  <MenuItem key={value} value={value}>
                    {value}
                  </MenuItem>
                ))}
              </Select>
              <Box>
                {schedule.repitation === "DAILY" && t("day(s)")}{" "}
                {schedule.repitation === "WEEKLY" && t("week(s)")}{" "}
                {schedule.repitation === "MONTHLY" && t("month(s)")}
              </Box>
            </Box>
            {errors.repeatEvery && (
              <FormHelperText error>{errors.repeatEvery}</FormHelperText>
            )}
          </FormControl>
        </Grid>
      </Grid>

      <Grid container spacing={4} sx={{ mb: 2 }}>
        <Grid item xs={12} md={3}>
          <FieldLabel label={t("Start Date")} required={true} />
          <DatePicker
            value={schedule.startDate ? parseISO(schedule.startDate) : null}
            disabled={isViewMode}
            minDate={isEditingSchedule ? undefined : today}
            onChange={(newValue) =>
              handleChange({
                target: {
                  name: "startDate",
                  value:
                    newValue && isValid(newValue)
                      ? format(newValue, "yyyy-MM-dd")
                      : null,
                },
              })
            }
            format={getDateInputFormat()} // reuse your existing util
            slots={{
              openPickerIcon: CalendarTodayIcon,
            }}
            slotProps={{
              textField: {
                fullWidth: true,
                required: true,
                placeholder: getDatePlaceholder(), // reuse your existing util
                error: !!errors.startDate,
                helperText: errors.startDate,
                sx: {
                  mt: "8px",
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "8px",
                    maxHeight: "44px",
                    "& fieldset": {
                      border: "1px solid #647490",
                    },
                    "&:hover fieldset": {
                      border: "1px solid #647490",
                    },
                    "&.Mui-focused fieldset": {
                      border: "1px solid #647490",
                    },
                    "& input": {
                      fontFamily: "Roboto",
                      fontWeight: 400,
                      fontSize: "16px",
                      color: "#111214",
                    },
                  },
                },
              },
            }}
          />
        </Grid>
        <Grid item xs={12} md={9}>
          <FieldLabel label={t("End Date")} required={true} />
          <RadioGroup
            name="endCondition"
            value={schedule.endCondition}
            onChange={handleChange}
            sx={{ display: "flex", flexDirection: "row", gap: 1, mt: "8px" }}
          >
            {/* Option 1: No End Date */}
            <FormControlLabel
              value="noEnd"
              control={
                <Radio
                  disabled={isViewMode}
                  sx={{
                    color: "#20A8E0",
                    "&.Mui-checked": { color: "#20A8E0" },
                  }}
                />
              }
              label={t("No End Date")}
              sx={{ fontFamily: "Roboto" }}
            />

            {/* Option 2: End by date */}
            <FormControlLabel
              value="byDate"
              control={
                <Radio
                  disabled={isViewMode}
                  sx={{
                    color: "#20A8E0",
                    "&.Mui-checked": { color: "#20A8E0" },
                  }}
                />
              }
              label={
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography sx={{ fontFamily: "Roboto", fontSize: "16px" }}>
                    {t("By")}
                  </Typography>
                  <DatePicker
                    value={schedule.endDate ? parseISO(schedule.endDate) : null}
                    disabled={isViewMode || schedule.endCondition !== "byDate"}
                    minDate={
                      isEditingSchedule
                        ? schedule.startDate
                          ? parseISO(schedule.startDate)
                          : undefined
                        : new Date(minimumEndDate)
                    }
                    onChange={(newValue) =>
                      handleChange({
                        target: {
                          name: "endDate",
                          value:
                            newValue && isValid(newValue)
                              ? format(newValue, "yyyy-MM-dd")
                              : null,
                        },
                      })
                    }
                    format={getDateInputFormat()}
                    slots={{ openPickerIcon: CalendarTodayIcon }}
                    slotProps={{
                      textField: {
                        size: "small",
                        disabled:
                          isViewMode || schedule.endCondition !== "byDate",
                        error: !!errors.endDate,
                        helperText: errors.endDate,
                        sx: {
                          "& .MuiOutlinedInput-root": {
                            borderRadius: "12px",
                            minHeight: "44px",
                            fontFamily: "Roboto",
                            fontSize: "14px",
                            "& fieldset": {
                              borderColor: "#183059",
                              borderWidth: "2px",
                            },
                            "&:hover fieldset": { borderColor: "#183059" },
                            "&.Mui-focused fieldset": {
                              borderColor: "#183059",
                            },
                            "&.Mui-disabled fieldset": {
                              borderColor: "#C4C4C4",
                            },
                          },
                        },
                      },
                    }}
                  />
                </Box>
              }
            />

            {/* Option 3: After N occurrences */}
            <FormControlLabel
              value="afterOccurrences"
              control={
                <Radio
                  disabled={isViewMode}
                  sx={{
                    color: "#20A8E0",
                    "&.Mui-checked": { color: "#20A8E0" },
                  }}
                />
              }
              label={
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography sx={{ fontFamily: "Roboto", fontSize: "16px" }}>
                    {t("After")}
                  </Typography>
                  <Select
                    size="small"
                    value={schedule.occurrences ?? 0}
                    disabled={
                      isViewMode || schedule.endCondition !== "afterOccurrences"
                    }
                    IconComponent={(props) => (
                      <svg
                        width="18"
                        height="10"
                        viewBox="0 0 18 10"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        style={{
                          marginRight: "10px",
                        }}
                      >
                        <path
                          d="M1 1L9 9L17 1"
                          stroke="#20A8E0"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    )}
                    onChange={(e) =>
                      handleChange({
                        target: { name: "occurrences", value: e.target.value },
                      })
                    }
                    sx={{
                      borderRadius: "12px",
                      fontFamily: "Roboto",
                      minHeight: "44px",
                      fontSize: "14px",
                      minWidth: "80px",
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#183059",
                        borderWidth: "2px",
                      },
                      "&:hover .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#183059",
                      },
                      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#183059",
                      },
                      "&.Mui-disabled .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#C4C4C4",
                      },
                    }}
                  >
                    <MenuItem value={0}>0</MenuItem>
                    {repeatEveryOptions.map((n) => (
                      <MenuItem key={n} value={n}>
                        {n}
                      </MenuItem>
                    ))}
                  </Select>
                  <Typography sx={{ fontFamily: "Roboto", fontSize: "16px" }}>
                    {t("occurrences")}
                  </Typography>
                </Box>
              }
            />
          </RadioGroup>
        </Grid>

        <Grid item xs={12} md={12}>
          {" "}
          {/* Conditional Date/Day Selection based on Frequency */}
          {schedule.repitation && (
            <Grid container spacing={12} sx={{ mb: 2 }}>
              {schedule.repitation === "WEEKLY" && (
                <Grid item xs={12} md={12}>
                  <FormControl
                    fullWidth
                    variant="outlined"
                    error={!!errors.weeklyDays}
                  >
                    <FieldLabel label={t("Occurs on")} />
                    <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                      {WEEKDAYS.map((day) => {
                        const isSelected = (schedule.weeklyDays || []).includes(
                          day.value,
                        );
                        return (
                          <Box
                            key={day.value}
                            onClick={() => {
                              if (isViewMode) return;
                              const current = schedule.weeklyDays || [];
                              const updated = isSelected
                                ? current.filter((d) => d !== day.value)
                                : [...current, day.value];
                              handleWeeklyDaysChange({
                                target: { value: updated },
                              });
                            }}
                            sx={{
                              width: "48px",
                              height: "36px",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              borderRadius: "8px",
                              border: "1.5px solid",
                              borderColor: isSelected ? "#20A8E0" : "#C4C4C4",
                              backgroundColor: isSelected
                                ? "#20A8E0"
                                : "#FFFFFF",
                              color: isSelected ? "#FFFFFF" : "#111214",
                              fontFamily: "Roboto",
                              fontWeight: 500,
                              fontSize: "14px",
                              cursor: isViewMode ? "default" : "pointer",
                              userSelect: "none",
                              transition: "all 0.15s ease",
                              "&:hover": !isViewMode
                                ? {
                                    borderColor: "#20A8E0",
                                    backgroundColor: isSelected
                                      ? "#1a90c4"
                                      : "#EAF6FC",
                                  }
                                : {},
                            }}
                          >
                            {day.short}
                          </Box>
                        );
                      })}
                    </Box>
                    {errors.weeklyDays && (
                      <FormHelperText error>{errors.weeklyDays}</FormHelperText>
                    )}
                  </FormControl>
                </Grid>
              )}

              {schedule.repitation === "MONTHLY" && (
                <Grid item xs={12} md={12}>
                  <FormControl
                    fullWidth
                    variant="outlined"
                    error={!!errors.monthlyDays}
                  >
                    <FieldLabel label={t("Occurs on")} />
                    <RadioGroup
                      name="monthlyType"
                      value={schedule.monthlyType || "dayOfMonth"}
                      onChange={handleChange}
                      sx={{
                        display: "flex",
                        flexDirection: "row",
                        gap: 1.5,
                        mt: "8px",
                      }}
                    >
                      {/* Option 1: Day X of the month */}
                      <FormControlLabel
                        value="dayOfMonth"
                        control={
                          <Radio
                            disabled={isViewMode}
                            sx={{
                              color: "#20A8E0",
                              "&.Mui-checked": { color: "#20A8E0" },
                            }}
                          />
                        }
                        label={
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1.5,
                            }}
                          >
                            <Typography
                              sx={{ fontFamily: "Roboto", fontSize: "16px" }}
                            >
                              {t("Day")}
                            </Typography>
                            <Select
                              size="small"
                              value={schedule.monthlyDay || 1}
                              disabled={
                                isViewMode ||
                                schedule.monthlyType !== "dayOfMonth"
                              }
                              onChange={(e) =>
                                handleChange({
                                  target: {
                                    name: "monthlyDay",
                                    value: e.target.value,
                                  },
                                })
                              }
                              IconComponent={(props) => (
                                <svg
                                  width="18"
                                  height="10"
                                  viewBox="0 0 18 10"
                                  fill="none"
                                  xmlns="http://www.w3.org/2000/svg"
                                  style={{
                                    marginRight: "10px",
                                  }}
                                >
                                  <path
                                    d="M1 1L9 9L17 1"
                                    stroke="#20A8E0"
                                    stroke-width="2"
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                  />
                                </svg>
                              )}
                              sx={{
                                minWidth: "80px",
                                minHeight: "44px",
                                borderRadius: "12px",
                                fontFamily: "Roboto",
                                fontSize: "14px",
                                "& .MuiOutlinedInput-notchedOutline": {
                                  borderColor: "#183059",
                                  borderWidth: "2px",
                                },
                                "&:hover .MuiOutlinedInput-notchedOutline": {
                                  borderColor: "#183059",
                                },
                                "&.Mui-focused .MuiOutlinedInput-notchedOutline":
                                  {
                                    borderColor: "#183059",
                                  },
                                "&.Mui-disabled .MuiOutlinedInput-notchedOutline":
                                  {
                                    borderColor: "#C4C4C4",
                                  },
                              }}
                            >
                              {Array.from({ length: 31 }, (_, i) => i + 1).map(
                                (day) => (
                                  <MenuItem key={day} value={day}>
                                    {day}
                                  </MenuItem>
                                ),
                              )}
                            </Select>
                            <Typography
                              sx={{ fontFamily: "Roboto", fontSize: "16px" }}
                            >
                              {t("of the month")}
                            </Typography>
                          </Box>
                        }
                      />

                      {/* Option 2: The First/Second/... Sunday/Monday/... of the month */}
                      <FormControlLabel
                        value="dayOfWeekOfMonth"
                        control={
                          <Radio
                            disabled={isViewMode}
                            sx={{
                              color: "#20A8E0",
                              "&.Mui-checked": { color: "#20A8E0" },
                            }}
                          />
                        }
                        label={
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1.5,
                              flexWrap: "wrap",
                            }}
                          >
                            <Select
                              size="small"
                              value={schedule.monthlyWeekOrder || "first"}
                              disabled={
                                isViewMode ||
                                schedule.monthlyType !== "dayOfWeekOfMonth"
                              }
                              onChange={(e) =>
                                handleChange({
                                  target: {
                                    name: "monthlyWeekOrder",
                                    value: e.target.value,
                                  },
                                })
                              }
                              IconComponent={(props) => (
                                <svg
                                  width="18"
                                  height="10"
                                  viewBox="0 0 18 10"
                                  fill="none"
                                  xmlns="http://www.w3.org/2000/svg"
                                  style={{
                                    marginRight: "10px",
                                  }}
                                >
                                  <path
                                    d="M1 1L9 9L17 1"
                                    stroke="#20A8E0"
                                    stroke-width="2"
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                  />
                                </svg>
                              )}
                              sx={{
                                minWidth: "110px",
                                minHeight: "44px",
                                borderRadius: "12px",
                                fontFamily: "Roboto",
                                fontSize: "14px",
                                "& .MuiOutlinedInput-notchedOutline": {
                                  borderColor: "#183059",
                                  borderWidth: "2px",
                                },
                                "&:hover .MuiOutlinedInput-notchedOutline": {
                                  borderColor: "#183059",
                                },
                                "&.Mui-focused .MuiOutlinedInput-notchedOutline":
                                  {
                                    borderColor: "#183059",
                                  },
                                "&.Mui-disabled .MuiOutlinedInput-notchedOutline":
                                  {
                                    borderColor: "#C4C4C4",
                                  },
                              }}
                            >
                              {[
                                { value: "first", label: t("First") },
                                { value: "second", label: t("Second") },
                                { value: "third", label: t("Third") },
                                { value: "fourth", label: t("Fourth") },
                                { value: "fifth", label: t("Fifth") },
                              ].map((opt) => (
                                <MenuItem key={opt.value} value={opt.value}>
                                  {opt.label}
                                </MenuItem>
                              ))}
                            </Select>

                            <Select
                              size="small"
                              value={schedule.monthlyWeekDay || "sunday"}
                              disabled={
                                isViewMode ||
                                schedule.monthlyType !== "dayOfWeekOfMonth"
                              }
                              onChange={(e) =>
                                handleChange({
                                  target: {
                                    name: "monthlyWeekDay",
                                    value: e.target.value,
                                  },
                                })
                              }
                              IconComponent={(props) => (
                                <svg
                                  width="18"
                                  height="10"
                                  viewBox="0 0 18 10"
                                  fill="none"
                                  xmlns="http://www.w3.org/2000/svg"
                                  style={{
                                    marginRight: "10px",
                                  }}
                                >
                                  <path
                                    d="M1 1L9 9L17 1"
                                    stroke="#20A8E0"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                  />
                                </svg>
                              )}
                              sx={{
                                minWidth: "120px",
                                minHeight: "44px",
                                borderRadius: "12px",
                                fontFamily: "Roboto",
                                fontSize: "14px",
                                "& .MuiOutlinedInput-notchedOutline": {
                                  borderColor: "#183059",
                                  borderWidth: "2px",
                                },
                                "&:hover .MuiOutlinedInput-notchedOutline": {
                                  borderColor: "#183059",
                                },
                                "&.Mui-focused .MuiOutlinedInput-notchedOutline":
                                  {
                                    borderColor: "#183059",
                                  },
                                "&.Mui-disabled .MuiOutlinedInput-notchedOutline":
                                  {
                                    borderColor: "#C4C4C4",
                                  },
                              }}
                            >
                              {[
                                { value: "sunday", label: t("Sunday") },
                                { value: "monday", label: t("Monday") },
                                { value: "tuesday", label: t("Tuesday") },
                                { value: "wednesday", label: t("Wednesday") },
                                { value: "thursday", label: t("Thursday") },
                                { value: "friday", label: t("Friday") },
                                { value: "saturday", label: t("Saturday") },
                              ].map((day) => (
                                <MenuItem key={day.value} value={day.value}>
                                  {day.label}
                                </MenuItem>
                              ))}
                            </Select>

                            <Typography
                              sx={{ fontFamily: "Roboto", fontSize: "16px" }}
                            >
                              {t("of the month")}
                            </Typography>
                          </Box>
                        }
                      />
                    </RadioGroup>

                    {errors.monthlyDays && (
                      <FormHelperText error>
                        {errors.monthlyDays}
                      </FormHelperText>
                    )}
                  </FormControl>
                </Grid>
              )}

              {/* {schedule.repitation === "ONETIME" && (
                <Grid item xs={12} md={12}>
                  <FormControl
                    fullWidth
                    variant="outlined"
                    error={!!errors.onetimeDate}
                  >
                    <FieldLabel label={t("Select Date")} required={true} />
                    <DatePicker
                      value={schedule.onetimeDate || null}
                      onChange={handleOnetimeDateChange}
                      disabled={isViewMode}
                      minDate={new Date()}
                      format={getDateInputFormat()}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          required: true,
                          error: !!errors.onetimeDate,
                          placeholder: getDatePlaceholder(),
                          helperText: errors.onetimeDate && errors.onetimeDate,
                          sx: {
                            "& .MuiOutlinedInput-root": {
                              borderRadius: "16px",
                              "& fieldset": {
                                borderColor: "#183059",
                                borderWidth: "2px",
                              },
                              "&:hover fieldset": {
                                borderColor: "#183059",
                              },
                              "&.Mui-focused fieldset": {
                                borderColor: "#183059",
                              },
                              "& input": {
                                fontFamily: "Roboto",
                                fontWeight: 400,
                                fontSize: "16px",
                                color: "#111214",
                              },
                            },
                          },
                        },
                      }}
                    />
                  </FormControl>
                </Grid>
              )} */}
            </Grid>
          )}
        </Grid>
      </Grid>

      <Grid container spacing={4} sx={{ mb: 4 }}>
        <Grid item xs={12}>
          <FieldLabel label={t("Description")} />
          <TextField
            name="description"
            value={schedule.description}
            disabled={isViewMode}
            onChange={handleChange}
            required
            fullWidth
            multiline
            rows={2}
            placeholder={t("Enter description here")}
            variant="outlined"
            inputProps={{
              style: {
                fontFamily: "Roboto",
                fontWeight: 400,
                fontSize: "16px",
                lineHeight: "100%",
                color: "#111214",
              },
            }}
            error={!!errors.description}
            sx={{
              mt: "8px",
              "& .MuiOutlinedInput-root": {
                borderRadius: "8px",
                "& fieldset": {
                  border: "1px solid #647490",
                },
              },
            }}
          />
          {errors.description && (
            <FormHelperText error>{errors.description}</FormHelperText>
          )}
        </Grid>
      </Grid>
    </LocalizationProvider>
  );
};

export default ScheduleDetailsForm;
